import * as React from 'react';
import {
    DataGrid,
    getGridStringOperators,
    getGridSingleSelectOperators,
} from '@mui/x-data-grid';
import { makeStyles } from "@mui/styles";
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';

import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';
import CustomButton from 'components/CustomButton';
import GridToolbar from 'components/GridToolbar';
import ContentHeader from 'components/ContentHeader';
import ListPagination from 'components/ListPagination';
import ListQuickSearch from 'components/ListQuickSearch';
import FilterList from './FilterList';
import { useSnackbar } from 'contexts';
import { useList } from "hooks/useList";
import { getInternalUsers, deleteUserById, getRoles } from "services";
import { getListActions, checkCreatePermission } from 'utils';
import { NonInternalUserRoles } from 'config';
import RoleSelect from 'components/RoleSelect';


const useStyles = makeStyles((theme) => ({
    tableHeader: {
        backgroundColor: theme.palette.primary.main,
        color: "white",
    }
}));

const BaseFilters = { includeTotal: true, include: ["role"] };

export default function InternalUsers() {
    const classes = useStyles();

    const snackbar = useSnackbar();

    const [activeItemId, setActiveItemId] = React.useState(null);
    const [roles, setRoles] = React.useState([]);

    const {
        loading,
        items,
        total,
        editItem,
        deleteItem,
        filters,
        setFilters,
        setSortModel,
        setPageNo,
        setPageSize,
    } = useList({
        getItems: getInternalUsers,
        initialFilters: BaseFilters,
        initialPageNo: 0,
        initialPageSize: 10,
        itemName: "internalUsers",
        deleteItemById: deleteUserById,
    });

    const handleFilterChange = ({ items }) => {
        const { columnField, value } = items[0];
        if (value) {
            setFilters({ ...BaseFilters, ...filters, [columnField]: value });
        } else {
            const newFilters = { ...BaseFilters, ...filters };
            delete newFilters[[columnField]];
            setFilters(newFilters);
        }
    };

    const handleRoleChange = React.useCallback((role) => {
        if (!role) {
            handleFilterChange({
                items: [{ columnField: 'role', value: undefined }]
            });
        }
        handleFilterChange({
            items: [{ columnField: 'role', value: role._id }]
        });

    });

    const handleSortModelChange = (newModel) => {

        if (newModel[0]) {
            const { field, sort } = newModel[0];
            setSortModel({ orderBy: field, order: sort });
        }
    };

    const handleConfirmationSuccess = () => {
        deleteItem(activeItemId);
        setActiveItemId(null);
    };

    const columns = React.useMemo(() => {
        return [
            {
                field: 'name',
                headerName: 'Name',
                flex: 1,
                headerClassName: classes.tableHeader,
                filterOperators: getGridStringOperators().filter(
                    (operator) => operator.value === 'contains',
                ),
            },
            {
                field: 'email',
                headerName: 'Email',
                flex: 1,
                headerClassName: classes.tableHeader,
                filterOperators: getGridStringOperators().filter(
                    (operator) => operator.value === 'contains',
                ),
            },
            {
                field: 'roles',
                headerName: 'Roles',
                flex: 1,
                headerClassName: classes.tableHeader,
                filterable: true,
                valueOptions: roles.map(role => ({
                    value: role._id, label: role.name
                })),
                filterOperators: getGridSingleSelectOperators().filter(
                    (operator) => operator.value === 'is',
                ),
                valueGetter: (params) => params?.row?.role?.name || '',
            },
            {
                field: 'contact',
                headerName: 'Contact',
                flex: 1,
                headerClassName: classes.tableHeader,
                filterOperators: getGridStringOperators().filter(
                    (operator) => operator.value === 'contains',
                ),
            },
            {
                field: 'actions',
                headerName: 'Actions',
                type: "actions",
                width: 80,
                headerClassName: classes.tableHeader,
                getActions: (params) =>
                    getListActions(params.id, editItem, setActiveItemId, "FieldOption")
            },
        ];
    }, [classes.tableHeader, roles]);


    React.useEffect(() => {
        (async () => {
            try {
                const { roles } = await getRoles();

                setRoles(roles.filter(role => !NonInternalUserRoles.has(role.name)));
            } catch (error) {
                console.log(error);
                snackbar.showSnackbar("Unable to get role options", "error");
            }
        })();
    }, []);

    return (
        <Box>
            <ContentHeader
                title="Super Admin"
                BaseFilters={BaseFilters}
                filters={filters}
                setFilters={setFilters}
                clearFilter
            >
                <FilterList>
                    <ListQuickSearch onFilterChange={handleFilterChange} value={filters[['name']]} />
                    <ListQuickSearch
                        onFilterChange={handleFilterChange}
                        label="Search by email"
                        field="email"
                        value={filters[['email']]}
                    />
                    <ListQuickSearch
                        onFilterChange={handleFilterChange}
                        label="Search by contact"
                        field="contact"
                        value={filters[['contact']]}
                    />
                    <RoleSelect onChange={handleRoleChange} defaultValue={filters[['role']]} />
                </FilterList>
                &nbsp;

                {checkCreatePermission("FieldOption") &&
                    <CustomButton
                        variant="contained"
                        onClick={editItem}
                    >
                        Create
                    </CustomButton>
                }
            </ContentHeader>
            <div style={{ height: '80vh', width: '100%', padding: 16 }}>
                <DataGrid
                    pagination
                    autoPageSize
                    paginationMode='server'
                    filterMode='server'
                    density='compact'
                    loading={loading}
                    rowCount={total}
                    onPageChange={setPageNo}
                    onPageSizeChange={setPageSize}
                    rows={items}
                    getRowId={(row) => row._id}
                    columns={columns}
                    sortingMode="server"
                    onSortModelChange={handleSortModelChange}
                    onFilterModelChange={handleFilterChange}
                    components={{
                        Toolbar: GridToolbar,
                        LoadingOverlay: LinearProgress,
                        Pagination: ListPagination,
                    }}
                    componentsProps={{
                        pagination: {
                            rowCount: total
                        }
                    }}
                />
            </div>
            <ConfirmationDialog
                message={
                    `This user will permanantly get deleted, Are you sure?`
                }
                open={!!activeItemId}
                title="Delete Internal User"
                primaryActionVariant="error"
                primaryAction="Yes"
                secondaryAction="Cancel"
                onClose={() => setActiveItemId(null)}
                onSuccess={handleConfirmationSuccess}
            />
        </Box>
    );
}